export interface WordAnalysis {
  word: string;
  status: 'correct' | 'incorrect' | 'extra' | 'missing';
  expected?: string;
}

export interface AnalysisResult {
  score: number;
  wordAnalysis: WordAnalysis[];
  feedbackTitle: string;
  feedbackMsg: string;
  feedbackColor: string;
}

export function analyzeSpeech(transcript: string, targetPrompt: string): AnalysisResult {
  const cleanText = (text: string) => text.toLowerCase().replace(/[.,/#!$%^&*;:{}=\-_`~()?"']/g, "").trim();
  const targetWords = cleanText(targetPrompt).split(/\s+/).filter(w => w.length > 0);
  const transcriptWords = cleanText(transcript).split(/\s+/).filter(w => w.length > 0);
  
  const originalTargetWords = targetPrompt.split(/\s+/).filter(w => w.length > 0);
  const originalTranscriptWords = transcript.split(/\s+/).filter(w => w.length > 0);

  const n = targetWords.length;
  const m = transcriptWords.length;
  
  // Handle empty cases
  if (n === 0) return { score: 0, wordAnalysis: [], feedbackTitle: "Error", feedbackMsg: "Empty prompt", feedbackColor: "text-red-600" };
  if (m === 0) {
    return { 
      score: 0, 
      wordAnalysis: originalTargetWords.map(w => ({ word: w, status: 'missing' })), 
      feedbackTitle: "Try Again", 
      feedbackMsg: "No speech detected.", 
      feedbackColor: "text-amber-600" 
    };
  }

  // DP for Levenshtein distance
  const dp = Array(n + 1).fill(null).map(() => Array(m + 1).fill(0));

  for (let i = 0; i <= n; i++) dp[i][0] = i;
  for (let j = 0; j <= m; j++) dp[0][j] = j;

  for (let i = 1; i <= n; i++) {
    for (let j = 1; j <= m; j++) {
      const tWord = targetWords[i - 1];
      const uWord = transcriptWords[j - 1];
      
      let cost = 2; // Substitution cost = 2 to prefer Ins/Del over mismatch
      if (tWord === uWord) {
        cost = 0;
      } else if (tWord.startsWith('[') && tWord.endsWith(']')) {
        cost = 0; // Placeholder matches anything
      }

      dp[i][j] = Math.min(
        dp[i - 1][j] + 1, // Deletion
        dp[i][j - 1] + 1, // Insertion
        dp[i - 1][j - 1] + cost // Substitution/Match
      );
    }
  }

  const distance = dp[n][m];
  const maxLen = Math.max(n, m);
  const accuracy = Math.max(0, Math.round((1 - distance / maxLen) * 100));

  // Backtrack to determine word correctness
  const wordAnalysis: WordAnalysis[] = [];
  let i = n;
  let j = m;
  
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0) {
      const tWord = targetWords[i - 1];
      const uWord = transcriptWords[j - 1];
      let cost = 2;
      if (tWord === uWord || (tWord.startsWith('[') && tWord.endsWith(']'))) {
        cost = 0;
      }
      
      // Prefer Match/Substitution if costs align
      if (dp[i][j] === dp[i - 1][j - 1] + cost) {
        if (cost === 0) {
          wordAnalysis.unshift({ word: originalTranscriptWords[j - 1], status: 'correct' });
        } else {
          // Substitution
          wordAnalysis.unshift({ 
            word: originalTranscriptWords[j - 1], 
            status: 'incorrect',
            expected: originalTargetWords[i - 1]
          });
        }
        i--; j--;
      } else if (dp[i][j] === dp[i - 1][j] + 1) {
        // Deletion
        wordAnalysis.unshift({ word: originalTargetWords[i - 1], status: 'missing' });
        i--;
      } else {
        // Insertion
        wordAnalysis.unshift({ word: originalTranscriptWords[j - 1], status: 'extra' });
        j--;
      }
    } else if (i > 0) {
      // Deletion
      wordAnalysis.unshift({ word: originalTargetWords[i - 1], status: 'missing' });
      i--;
    } else {
      // Insertion
      wordAnalysis.unshift({ word: originalTranscriptWords[j - 1], status: 'extra' });
      j--;
    }
  }

  // Determine feedback based on accuracy
  let feedbackTitle = "Keep Practicing";
  let feedbackMsg = "Try to speak clearly and match the prompt exactly.";
  let feedbackColor = "text-blue-600";
  
  const hasExtras = wordAnalysis.some(w => w.status === 'extra');
  const hasIncorrect = wordAnalysis.some(w => w.status === 'incorrect');
  const hasMissing = wordAnalysis.some(w => w.status === 'missing');
  
  if (accuracy === 100) {
    feedbackTitle = "Excellent!";
    feedbackMsg = "Your pronunciation is spot on. Great job!";
    feedbackColor = "text-emerald-600";
  } else if (!hasIncorrect && !hasMissing && hasExtras) {
    feedbackTitle = "Almost Perfect";
    feedbackMsg = "You said all the right words, but added some extra ones. Try to match the prompt exactly.";
    feedbackColor = "text-blue-600";
  } else if (accuracy >= 80) {
    feedbackTitle = "Great Job";
    feedbackMsg = "You're very close! Watch out for small differences.";
    feedbackColor = "text-emerald-600";
  } else if (accuracy >= 60) {
    feedbackTitle = "Good Effort";
    feedbackMsg = "Focus on the highlighted words you missed.";
    feedbackColor = "text-blue-600";
  } else {
    feedbackTitle = "Needs Improvement";
    feedbackMsg = "We missed a few words. Try speaking slower and clearer.";
    feedbackColor = "text-amber-600";
  }

  return { score: accuracy, wordAnalysis, feedbackTitle, feedbackMsg, feedbackColor };
}
